import { z } from "zod";

// --- Enums / literal unions -------------------------------------------------

export const sourceInputModeSchema = z.enum([
  "official_url_pdf",
  "admin_upload_pdf",
  "official_html_page"
]);
export type SourceInputMode = z.infer<typeof sourceInputModeSchema>;

export const pdfKindSchema = z.enum([
  "digital_pdf",
  "scanned_pdf",
  "mixed_pdf",
  "answer_key_pdf"
]);
export type PdfKind = z.infer<typeof pdfKindSchema>;

export const importJobStatusSchema = z.enum([
  "queued",
  "fetching",
  "stored",
  "extracting",
  "needs_review",
  "approved",
  "published",
  "failed"
]);
export type ImportJobStatus = z.infer<typeof importJobStatusSchema>;

export const candidateStatusSchema = z.enum([
  "draft",
  "needs_editor_review",
  "needs_reviewer_approval",
  "approved",
  "rejected"
]);
export type CandidateStatus = z.infer<typeof candidateStatusSchema>;

// --- Inputs / messages ------------------------------------------------------

export const sourceDocumentInputSchema = z.object({
  examTypeSlug: z.string().min(1),
  sourceUrl: z.string().url().optional(),
  inputMode: sourceInputModeSchema,
  pdfKind: pdfKindSchema.optional(),
  year: z.number().int().min(1900).max(2200).optional(),
  institution: z.string().min(1),
  permissionProfileId: z.string().optional()
});
export type SourceDocumentInput = z.infer<typeof sourceDocumentInputSchema>;

export const importJobMessageSchema = z.object({
  importJobId: z.string().min(1),
  sourceDocumentId: z.string().min(1),
  requestedBy: z.string().min(1),
  inputMode: sourceInputModeSchema,
  createdAt: z.string().min(1)
});
export type ImportJobMessage = z.infer<typeof importJobMessageSchema>;

export const questionImportCandidateSchema = z.object({
  id: z.string(),
  sourceDocumentId: z.string(),
  importJobId: z.string(),
  questionNumber: z.number().int().positive().optional(),
  stemText: z.string().optional(),
  optionTexts: z.array(z.string()),
  answerKey: z.string().optional(),
  confidence: z.number().min(0).max(1),
  status: candidateStatusSchema
});
export type QuestionImportCandidate = z.infer<typeof questionImportCandidateSchema>;

export const questionImportCandidateListSchema = z.array(questionImportCandidateSchema);

export const sourceConnectorConfigSchema = z.object({
  slug: z.string().min(1),
  institution: z.string().min(1),
  baseUrl: z.string().url(),
  allowedPaths: z.array(z.string()).default([]),
  blockedPaths: z.array(z.string()).default([]),
  rateLimitPerMinute: z.number().int().positive().max(600).default(30),
  permissionProfileId: z.string().optional()
});
export type SourceConnectorConfigInput = z.infer<typeof sourceConnectorConfigSchema>;

// Final question shape handed to the publish target (main platform's publish_questions).
export const finalQuestionSchema = z.object({
  candidateId: z.string(),
  sourceDocumentId: z.string(),
  examTypeSlug: z.string(),
  institution: z.string(),
  year: z.number().int().optional(),
  questionNumber: z.number().int().optional(),
  stemText: z.string().min(1),
  optionTexts: z.array(z.string()),
  answerKey: z.string().optional(),
  sourceContentLocale: z.string().optional()
});
export type FinalQuestion = z.infer<typeof finalQuestionSchema>;

export const discoverRequestSchema = z.object({
  connector: sourceConnectorConfigSchema,
  seedPaths: z.array(z.string()).optional()
});
export type DiscoverRequest = z.infer<typeof discoverRequestSchema>;
